import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CompletionData {
  moduleId: string;
  quizScore: number;
  timeSpentMinutes: number;
}

export const useTrainingCompletion = () => {
  const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
  const { toast } = useToast();

  const completeTraining = async (data: CompletionData) => {
    try {
      setIsGeneratingCertificate(true);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('User not authenticated');

      // Get user profile for name
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('full_name, first_name, last_name, email')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      // Get module details and certificate template
      const { data: module, error: moduleError } = await supabase
        .from('training_modules')
        .select('title_en, title_el')
        .eq('id', data.moduleId)
        .single();

      if (moduleError) throw moduleError;

      // Get certificate template for this module
      const { data: certificateTemplate, error: templateError } = await supabase
        .from('certificate_templates')
        .select('*')
        .eq('module_id', data.moduleId)
        .maybeSingle();

      if (templateError) {
        console.error('Error fetching certificate template:', templateError);
      }

      // Determine badge level using the database function
      const { data: badgeResult, error: badgeError } = await supabase
        .rpc('get_badge_level', { score: data.quizScore });

      if (badgeError) throw badgeError;

      const badgeLevel = badgeResult;
      const employeeName = profile.full_name || `${profile.first_name} ${profile.last_name}`;

      // Generate certificate URL if template exists
      let certificateUrl = null;
      if (certificateTemplate && badgeLevel) {
        // Create a certificate URL that points to a certificate viewer page
        certificateUrl = `/training/certificate/${data.moduleId}/${user.id}`;
      }

      // Save completion record
      const { data: completion, error: completionError } = await supabase
        .from('training_completions')
        .upsert({
          user_id: user.id,
          module_id: data.moduleId,
          quiz_score: data.quizScore,
          badge_level: badgeLevel,
          time_spent_minutes: data.timeSpentMinutes,
          completed_at: new Date().toISOString(),
          certificate_url: certificateUrl
        })
        .select()
        .single();

      if (completionError) throw completionError;

      // Show completion message
      if (badgeLevel) {
        if (certificateTemplate) {
          toast({
            title: "Training Completed!",
            description: `Congratulations! You earned a ${badgeLevel} badge and your certificate is ready to view.`,
            variant: "default"
          });
        } else {
          toast({
            title: "Training Completed!",
            description: `Congratulations! You earned a ${badgeLevel} badge.`,
            variant: "default"
          });
        }
      } else {
        toast({
          title: "Training Completed",
          description: "Training completed but minimum score not reached for badge.",
          variant: "default"
        });
      }

      return { success: true, completion, badgeLevel };

    } catch (error) {
      console.error('Error completing training:', error);
      toast({
        title: "Error",
        description: "Failed to complete training. Please try again.",
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsGeneratingCertificate(false);
    }
  };

  return {
    completeTraining,
    isGeneratingCertificate
  };
};